'use client';

import { useCallback, useEffect, type JSX } from 'react';

import { Text } from '@videoblocks/design-system';

import { ConfirmCreditSpendModal } from '../../_shared/_components/Modals/ConfirmCreditSpendModal';
import { EnterpriseFeatureAccessModal } from '../../_shared/_components/Modals/EnterpriseFeatureAccessModal';
import { EnterpriseInsufficientCreditsModal } from '../../_shared/_components/Modals/EnterpriseInsufficientCreditsModal';
import { InsufficientCreditsModal } from '../../_shared/_components/Modals/InsufficientCreditsModal';
import { RegenerateConfirmationModal } from '../../_shared/_components/Modals/RegenerateConfirmationModal';
import { AiToolkitToast } from '../../_shared/_components/Toast/AiToolkitToast';
import { AspectRatioSelectField } from '../../_shared/_components/forms/AspectRatioSelectField';
import { DurationSelectField } from '../../_shared/_components/forms/DurationSelectField';
import { FileUploadInputField } from '../../_shared/_components/forms/FileUploadInputField';
import { ModelSelectField } from '../../_shared/_components/forms/ModelSelectField';
import { ResolutionSelectField } from '../../_shared/_components/forms/ResolutionSelectField';
import { TextAreaInputField } from '../../_shared/_components/forms/TextAreaInputField/TextAreaInputField';
import { useAiToolkitContext } from '../../_shared/_context/AiToolkitContext';
import {
    DISABLED_INPUT_REASONS,
    MAX_PROMPT_LENGTH,
    VIDEO_GENERATION_MAX_FILE_SIZE_BYTES,
    VIDEO_GENERATION_MODEL_OPTIONS,
} from '../../_shared/_context/constants';
import { AI_TOOLKIT_CATEGORIES } from '../../_shared/_context/types';
import { AiToolkitSidePanel } from '../../_shared/_layouts/AiToolkitSidePanel';
import { useVideoGenerationForm } from './useVideoGenerationForm';

export const VideoGenerationSidePanel = (): JSX.Element => {
    const {
        formState,
        isValid,
        errors,
        promptState,
        promptHelperText,
        isSubmitDisabled,
        isDurationLocked,
        calculatedCost,
        onPromptChange,
        onPromptBlur,
        onAspectRatioChange,
        onDurationChange,
        onResolutionChange,
        onFileSelect,
        onFileRemove,
        onFileError,
        onSubmit,
        onConfirm,
    } = useVideoGenerationForm();
    const { activeTask, acknowledgeTask, submitStates, dispatchConfirmationModal, credits, isEnterprise, imageSearch } =
        useAiToolkitContext();
    const { setShowSearch, setSearchTerm } = imageSearch;
    const { status } = submitStates[AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION];
    const isSuccess = status === 'success';
    const isFormDisabled = !!activeTask && activeTask.status === 'inProgress';
    const isGenerateButtonDisabled = isSubmitDisabled || isFormDisabled;
    const isModelLocked = VIDEO_GENERATION_MODEL_OPTIONS.length <= 1;
    const libraryImageThumbnail = formState.selectedLibraryImage
        ? { thumbnailUrl: formState.selectedLibraryImage.thumbnailUrl, fileName: formState.selectedLibraryImage.title }
        : null;
    const showOtherTaskToast = activeTask !== null && activeTask.category !== AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION;

    useEffect(() => {
        if (status === 'success' || status === 'error') {
            acknowledgeTask(AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION, status);
        }
    }, [status, acknowledgeTask]);

    const handleFileSelect = useCallback(
        (file: File | File[]): void => {
            if (!Array.isArray(file)) {
                void onFileSelect(file);
            }
        },
        [onFileSelect],
    );

    const handleSubmit = useCallback((): void => {
        if (calculatedCost > credits) {
            dispatchConfirmationModal({
                type: 'OPEN_MODAL',
                modal: {
                    type: isEnterprise ? 'entInsufficientCredits' : 'insufficientCredits',
                    category: AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION,
                },
            });
            return;
        }
        if (isSuccess && isValid) {
            dispatchConfirmationModal({
                type: 'OPEN_MODAL',
                modal: { type: 'regenerate', category: AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION },
            });
        } else {
            onSubmit();
        }
    }, [calculatedCost, credits, isEnterprise, dispatchConfirmationModal, isSuccess, onSubmit, isValid]);

    return (
        <AiToolkitSidePanel
            aiToolkitCategory={AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION}
            onSubmit={handleSubmit}
            isSubmitDisabled={isGenerateButtonDisabled}
        >
            <>
                <div className="w-full flex flex-col gap-6">
                    <div className="hidden md:block shrink-0 dark:bg-neutral-dark-200 p-3 rounded-sm">
                        <Text asChild fontSize="sm" className="text-center">
                            <h2>Video Generator</h2>
                        </Text>
                    </div>
                    <TextAreaInputField
                        placeholder="Describe the video you want to generate. Be specific about camera movements, scene details, and mood..."
                        maxLength={MAX_PROMPT_LENGTH}
                        value={formState.prompt}
                        state={promptState}
                        helperText={promptHelperText}
                        onChange={onPromptChange}
                        onBlur={onPromptBlur}
                        disabled={isFormDisabled}
                    />
                    <FileUploadInputField
                        fileInputClassName="min-w-0 w-full"
                        acceptOptions={['.jpeg', '.jpg', '.png', '.webp']}
                        toolkitCategory={AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION}
                        label="Start frame (optional)"
                        maxSizeBytes={VIDEO_GENERATION_MAX_FILE_SIZE_BYTES}
                        error={errors.file}
                        value={formState.file}
                        libraryImage={libraryImageThumbnail}
                        onFileSelect={handleFileSelect}
                        onFileRemove={onFileRemove}
                        onFileError={onFileError}
                        onBrowseLibrary={() => {
                            setSearchTerm('');
                            setShowSearch(true);
                        }}
                        disabled={isFormDisabled}
                    />
                    <ModelSelectField
                        modelValue={formState.model}
                        modelVersion={VIDEO_GENERATION_MODEL_OPTIONS[0].version}
                        modelName={VIDEO_GENERATION_MODEL_OPTIONS[0].name}
                        modelDescription={VIDEO_GENERATION_MODEL_OPTIONS[0].description}
                        className="w-full"
                        disabled={isModelLocked}
                        disabledReason={isModelLocked ? DISABLED_INPUT_REASONS.singleModel : undefined}
                    />
                    <div className="w-full flex gap-3">
                        <AspectRatioSelectField
                            aiToolkitCategory={AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION}
                            triggerClassName="w-full min-w-0"
                            value={formState.aspectRatio}
                            onValueChange={onAspectRatioChange}
                            disabled={isFormDisabled}
                        />
                        <DurationSelectField
                            aiToolkitCategory={AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION}
                            triggerClassName="w-full min-w-0"
                            value={formState.duration}
                            onValueChange={onDurationChange}
                            disabled={isFormDisabled || isDurationLocked}
                            disabledReason={isDurationLocked ? DISABLED_INPUT_REASONS.durationLocked : undefined}
                        />
                    </div>
                    <ResolutionSelectField
                        aiToolkitCategory={AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION}
                        triggerClassName="w-full"
                        value={formState.resolution}
                        onValueChange={onResolutionChange}
                        disabled={isFormDisabled}
                    />
                </div>
                <AiToolkitToast open={showOtherTaskToast} activeTask={activeTask} />
                <ConfirmCreditSpendModal onConfirm={onConfirm} />
                <InsufficientCreditsModal />
                <EnterpriseInsufficientCreditsModal />
                <EnterpriseFeatureAccessModal />
                <RegenerateConfirmationModal onConfirm={onConfirm} />
            </>
        </AiToolkitSidePanel>
    );
};
